import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom';
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaFileExcel, 
  FaSearch,
  FaCalendarAlt,
  FaDownload,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Table, Pagination } from 'react-bootstrap'
import {getReport, getReports} from "../../context/photos/PhotoActions";
import {getReportSession, getSessions} from "../../context/users/UserActions";
import './ReportList.css'

// eslint-disable-next-line
export default function ReportList() {

    const [reports, setReports] = useState([])
    const [allReports, setAllReports] = useState([])
    const [filteredReports, setFilteredReports] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const { users: sessions, authenticatedUser, setUsers, setAlert } = useUserContext()

    const [keywords, setKeywords] = useState('')
    
    const handleChangeKeywords = (e) => {
        setKeywords(e.target.value)
    }

    // Función para normalizar texto (quitar acentos)
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
    }

    window.scrollTo({
        top: 0,
        behavior: 'auto',
    })

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const response = await getSessions({ path: 'sessions', token: authenticatedUser.value, page});
            if (response.error || !Array.isArray(response.data)) {
                setAlert({
                    message: response.message || 'Error al cargar los reportes. Si persiste el error, por favor contacte a soporte.',
                    msgType: 'warning'
                })
            } else {
                const sortedReports = response.data.sort((a, b) => b.sessionId - a.sessionId)
                setReports(sortedReports)
                setAllReports(sortedReports)
                setFilteredReports(sortedReports)
                setHasMore(response.paging.hasMore)
            }
        } catch (error) {
            setAlert({
                message: 'Error al cargar los reportes. Por favor, inténtalo de nuevo.',
                msgType: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [page])

    // Efecto para filtrar reportes cuando cambian las keywords
    useEffect(() => {
        if (!keywords.trim()) {
            setFilteredReports(allReports)
        } else {
            const filtered = allReports.filter(report => {
                const searchTerm = normalizeText(keywords)
                return (
                    normalizeText(report.sessionName || '').includes(searchTerm) ||
                    normalizeText(report.sessionCode || '').includes(searchTerm) ||
                    normalizeText(report.sessionDate || '').includes(searchTerm)
                )
            })
            setFilteredReports(filtered)
        }
    }, [keywords, allReports])


    const handleOnDownloadReport = async (sessionCode) => {
        const data = await getReportSession({ path: 'canvasReport', sessionCode, token: authenticatedUser.value })

        const response = await getReport(data.data);

        if (response.status === 200) {
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'reporte.xlsx';
            link.click();
            window.URL.revokeObjectURL(url);
        }
    }

    const nextPage = () => {
        if(hasMore){
            setPage(page + 1)
        }
    }
    const prevPage = () => {
        if(page > 1 ){
            setPage(page - 1)
        }
    }

    return (
        <div className="reports-container">
            <div className="container">
                {/* Header Section */}
                <div className="reports-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <div className="icon-circle">
                                <FaFileExcel className="icon" />
                            </div>
                        </div>
                        <div className="header-text">
                            <h1 className="header-title">Reporte de sesiones</h1>
                            <p className="header-subtitle">Descarga reportes de las sesiones realizadas</p>
                        </div>
                    </div>
                </div>

                {/* Actions Section */}
                <Card className="actions-card">
                    <Card.Body>
                        <Row>
                            <Col lg={6} md={8} sm={12}>
                                <div className="search-input-container">
                                    <FaSearch className="search-icon" />
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por nombre, código o fecha de sesión..."
                                        value={keywords}
                                        onChange={handleChangeKeywords}
                                        className="search-input"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table Section */}
                <Card className="table-card">
                    <Card.Body>
                        {isLoading ? (
                            <div className="loading-state">
                                <FaFileExcel className="loading-icon" />
                                <p className="mt-3">Cargando reportes...</p>
                            </div>
                        ) : filteredReports.length === 0 ? (
                            <div className="empty-state">
                                <FaFileExcel className="empty-icon" />
                                <h4>No se encontraron reportes</h4>
                                <p>Intenta con otros términos de búsqueda o verifica que existan sesiones.</p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table View */}
                                <div className="table-responsive d-none d-lg-block">
                                    <Table className="reports-table">
                                        <thead>
                                            <tr>
                                                <th>Sesión Id</th>
                                                <th>Nombre de la sesión</th>
                                                <th>Fecha y hora</th>
                                                <th>Descargar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredReports.map((report, index) => (
                                                <tr key={report.sessionId}>
                                                    <td className="session-id">#{report.sessionCode}</td>
                                                    <td className="session-name">{report.sessionName || '-'}</td>
                                                    <td className="session-date">{report.sessionDate || '-'}</td>
                                                    <td className="session-actions">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleOnDownloadReport(report.sessionCode)}
                                                            className="download-btn"
                                                            title="Descargar reporte"
                                                        >
                                                            <FaDownload />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Tablet Table View */}
                                <div className="table-responsive d-none d-md-block d-lg-none">
                                    <Table className="reports-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre de la sesión</th>
                                                <th>Fecha</th>
                                                <th>Descargar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredReports.map((report, index) => (
                                                <tr key={report.sessionId}>
                                                    <td className="session-name">{report.sessionName || '-'}</td>
                                                    <td className="session-date">{report.sessionDate || '-'}</td>
                                                    <td className="session-actions">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleOnDownloadReport(report.sessionCode)}
                                                            className="download-btn"
                                                            title="Descargar reporte"
                                                        >
                                                            <FaDownload />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="reports-cards d-block d-md-none">
                                    {filteredReports.map((report, index) => (
                                        <Card key={report.sessionId} className="report-card">
                                            <Card.Body>
                                                <div className="report-card-content">
                                                    <div className="report-info">
                                                        <div className="report-avatar">
                                                            <div className="avatar-circle">
                                                                <span className="avatar-text">#{report.sessionCode}</span>
                                                            </div>
                                                        </div>
                                                        <div className="report-details">
                                                            <div className="report-name">
                                                                {report.sessionName || '-'}
                                                            </div>
                                                            <div className="report-date">
                                                                {report.sessionDate || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="report-actions">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleOnDownloadReport(report.sessionCode)}
                                                            className="download-btn-mobile"
                                                            title="Descargar reporte"
                                                        >
                                                            <FaDownload />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {/* Pagination */}
                {filteredReports.length > 0 && (
                    <div className="pagination-container">
                        <div className="pagination-wrapper">
                            {/* First Page */}
                            <button 
                                className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
                                onClick={() => setPage(1)}
                                disabled={page <= 1}
                                title="Ir al inicio"
                            >
                                <FaChevronLeft />
                                <FaChevronLeft />
                            </button>
                            
                            {/* Previous Page */}
                            <button 
                                className={`pagination-nav ${page <= 1 ? 'disabled' : ''}`}
                                onClick={prevPage}
                                disabled={page <= 1}
                                title="Página anterior"
                            >
                                <FaChevronLeft />
                            </button>
                            
                            {/* Page Numbers */}
                            <div className="page-numbers">
                                {page > 1 && (
                                    <button 
                                        className="page-number"
                                        onClick={() => setPage(page - 1)}
                                    >
                                        {page - 1}
                                    </button>
                                )}
                                
                                <button className="page-number active">
                                    {page}
                                </button>
                                
                                {hasMore && (
                                    <button 
                                        className="page-number"
                                        onClick={() => setPage(page + 1)}
                                    >
                                        {page + 1}
                                    </button>
                                )}
                            </div>
                            
                            {/* Next Page */}
                            <button 
                                className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
                                onClick={nextPage}
                                disabled={!hasMore}
                                title="Página siguiente"
                            >
                                <FaChevronRight />
                            </button>
                            
                            {/* Last Page */}
                            <button 
                                className={`pagination-nav ${!hasMore ? 'disabled' : ''}`}
                                onClick={() => setPage(page + 1)}
                                disabled={!hasMore}
                                title="Ir al final"
                            >
                                <FaChevronRight />
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
